import * as React from "react";
import { createPortal } from "react-dom";
import "../styleSheets/toolTipButton.css";

interface TooltipButtonProps {
  label: React.ReactNode;
  id?: string;
  tooltip?: string;
  disabled?: boolean;
  onClick?: () => void;
  buttonClassName?: string;
  buttonIcon?: boolean;
  buttonIconClassName?: string;
}

export function TooltipButton({
  label,
  id = undefined,
  tooltip,
  disabled = false,
  onClick,
  buttonClassName = "",
  buttonIcon = false,
  buttonIconClassName = "",
}: TooltipButtonProps) {
  const [show, setShow] = React.useState(false);
  const [coords, setCoords] = React.useState({ top: 0, left: 0 });
  const wrapperRef = React.useRef<HTMLSpanElement | null>(null);
  const tooltipId = React.useId();
  const tooltipRoot =
    typeof document !== "undefined"
      ? document.getElementById("tooltip-root")
      : null;

  const shouldExposeTooltip = !!tooltip && disabled;
  const isAriaDisabled = disabled && shouldExposeTooltip;

  const updateTooltipPosition = () => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 6,
      left: rect.left,
    });
  };

  const showTooltip = () => {
    if (!shouldExposeTooltip) return;
    updateTooltipPosition();
    setShow(true);
  };

  const hideTooltip = () => {
    if (!shouldExposeTooltip) return;
    setShow(false);
  };

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
  };

  return (
    <>
      <span ref={wrapperRef} style={{ display: "inline-block" }}>
        <button
          id={id || undefined}
          type="button"
          className={buttonClassName}
          onClick={handleClick}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          disabled={disabled && !shouldExposeTooltip}
          aria-disabled={isAriaDisabled || undefined}
          aria-describedby={shouldExposeTooltip ? tooltipId : undefined}
        >
          {buttonIcon ? (
            <div aria-hidden="true" className={buttonIconClassName} />
          ) : null}
          {label}
        </button>
      </span>

      {show && tooltipRoot
        ? createPortal(
            <div
              id={tooltipId}
              className="tooltip-portal"
              role="tooltip"
              style={{
                top: coords.top,
                left: coords.left,
              }}
            >
              {tooltip}
            </div>,
            tooltipRoot,
          )
        : null}
    </>
  );
}
